import { Upsell } from '@/@types/Upsell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, ChevronLeft, ChevronRight, ExternalLink, Link2, Plus, TrendingDown, TrendingUp, Unlink, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

type ConnectionTarget =
  | { type: 'upsell'; upsellPage: string }
  | { type: 'external'; url: string };

export interface CurrentConnection {
  type: 'upsell' | 'external' | 'none';
  targetName?: string;
  targetUrl?: string;
}

interface ConnectionMenuProps {
  isOpen: boolean;
  direction: 'accept' | 'decline';
  upsellId: string;
  upsellName: string;
  availableUpsells: Upsell[];
  currentConnection?: CurrentConnection;
  onClose: () => void;
  onConnect: (
    sourceUpsellId: string,
    direction: 'accept' | 'decline',
    target: ConnectionTarget,
  ) => Promise<void>;
  onDisconnect?: (
    upsellId: string,
    direction: 'accept' | 'decline',
  ) => Promise<void>;
  onCreateNew: () => void;
}

export function ConnectionMenu({
  isOpen,
  direction,
  upsellId,
  upsellName,
  availableUpsells,
  currentConnection,
  onClose,
  onConnect,
  onDisconnect,
  onCreateNew,
}: ConnectionMenuProps) {
  const { t } = useTranslation('funnel');
  const [mode, setMode] = useState<'menu' | 'existing' | 'external'>('menu');
  const [externalUrl, setExternalUrl] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const dirLabel = direction === 'accept' ? t('accept_label') : t('decline_label');
  const dirColor = direction === 'accept' ? 'emerald' : 'red';

  const [listPage, setListPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  const filteredUpsells = useMemo(() => {
    return availableUpsells.filter((u) => {
      if (String(u.id) === String(upsellId)) return false;
      if (searchQuery) {
        return u.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    });
  }, [availableUpsells, upsellId, searchQuery]);

  const totalListPages = Math.ceil(filteredUpsells.length / ITEMS_PER_PAGE);
  const paginatedUpsells = filteredUpsells.slice(
    (listPage - 1) * ITEMS_PER_PAGE,
    listPage * ITEMS_PER_PAGE,
  );

  const handleConnectExisting = async (targetUpsell: Upsell) => {
    setIsConnecting(true);
    try {
      await onConnect(upsellId, direction, {
        type: 'upsell',
        upsellPage: targetUpsell.page,
      });
      handleClose();
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnectExternal = async () => {
    if (!externalUrl) return;
    setIsConnecting(true);
    try {
      await onConnect(upsellId, direction, {
        type: 'external',
        url: externalUrl,
      });
      handleClose();
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!onDisconnect) return;
    setIsConnecting(true);
    try {
      await onDisconnect(upsellId, direction);
      handleClose();
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClose = () => {
    setMode('menu');
    setExternalUrl('');
    setSearchQuery('');
    setListPage(1);
    onClose();
  };

  const hasConnection = currentConnection && currentConnection.type !== 'none';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            onClick={handleClose}
          >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-[380px] max-h-[85vh] rounded-xl bg-[#18181B] border border-[#27272a] shadow-2xl overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-[#27272a]">
              <div>
                <h3 className="text-sm font-semibold text-white">
                  {t('connection_title', { direction: dirLabel })}
                </h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  {upsellName}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-[#27272a] transition-colors"
              >
                <X className="h-3.5 w-3.5 text-zinc-500" />
              </button>
            </div>

            <div className="p-3">
              {mode === 'menu' && (
                <div className="space-y-1.5">
                  {hasConnection && (
                    <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3 mb-2">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-amber-300">
                            {t('connection_existing')}
                          </p>
                          <p className="text-[10px] text-amber-400/70 mt-0.5">
                            {currentConnection?.type === 'upsell'
                              ? t('connection_connected_to', { name: currentConnection.targetName })
                              : t('connection_redirecting_to', { url: currentConnection?.targetUrl })
                            }
                          </p>
                          <p className="text-[9px] text-zinc-500 mt-1">
                            {t('connection_replace_warning')}
                          </p>
                        </div>
                      </div>

                      {onDisconnect && (
                        <button
                          onClick={handleDisconnect}
                          disabled={isConnecting}
                          className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md bg-red-500/10 border border-red-500/20 py-1.5 text-[10px] font-medium text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="h-3 w-3" />
                          {isConnecting ? t('connection_disconnecting') : t('connection_remove_current')}
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => {
                      handleClose();
                      onCreateNew();
                    }}
                    className="w-full flex items-center gap-3 rounded-lg bg-[#09090b] border border-[#27272a] p-3 text-left hover:border-primary/40 transition-colors"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 ring-1 ring-primary/30 flex-shrink-0">
                      <Plus className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">
                        {hasConnection ? t('connection_replace_new') : t('connection_create_new')}
                      </p>
                      <p className="text-[10px] text-zinc-500">
                        {t('connection_create_new_desc')}
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => setMode('existing')}
                    className={`w-full flex items-center gap-3 rounded-lg bg-[#09090b] border border-[#27272a] p-3 text-left hover:border-${dirColor}-500/40 transition-colors`}
                  >
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-${dirColor}-500/15 ring-1 ring-${dirColor}-500/30 flex-shrink-0`}>
                      <Link2 className={`h-4 w-4 text-${dirColor}-400`} />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">
                        {hasConnection ? t('connection_replace_existing') : t('connection_connect_existing')}
                      </p>
                      <p className="text-[10px] text-zinc-500">
                        {t('connection_connect_existing_desc')}
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => setMode('external')}
                    className="w-full flex items-center gap-3 rounded-lg bg-[#09090b] border border-[#27272a] p-3 text-left hover:border-blue-500/40 transition-colors"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/15 ring-1 ring-blue-500/30 flex-shrink-0">
                      <ExternalLink className="h-4 w-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">
                        {hasConnection ? t('connection_replace_external') : t('connection_external_page')}
                      </p>
                      <p className="text-[10px] text-zinc-500">
                        {t('connection_external_desc')}
                      </p>
                    </div>
                  </button>
                </div>
              )}

              {mode === 'existing' && (
                <div className="space-y-3">
                  <button
                    onClick={() => setMode('menu')}
                    className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    {t('connection_back')}
                  </button>

                  <Input
                    placeholder={t('connection_search')}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setListPage(1);
                    }}
                    className="text-xs h-9"
                  />

                  <div className="space-y-1">
                    {filteredUpsells.length === 0 && (
                      <p className="text-[11px] text-zinc-500 text-center py-4">
                        {t('connection_no_available')}
                      </p>
                    )}
                    {paginatedUpsells.map((u) => {
                      const isUp = u.funnel_type === 'up_sell';
                      const TypeIcon = isUp ? TrendingUp : TrendingDown;
                      const hasAccept = !!u.accepted_page;
                      const hasDecline = !!u.recused_page;
                      const hasAnyConnection = hasAccept || hasDecline;
                      return (
                        <button
                          key={u.id}
                          onClick={() => handleConnectExisting(u)}
                          disabled={isConnecting}
                          className="w-full flex items-center gap-2.5 rounded-lg bg-[#09090b] border border-[#27272a] p-2.5 text-left hover:border-zinc-600 transition-colors disabled:opacity-50"
                        >
                          <div
                            className={`flex h-7 w-7 items-center justify-center rounded-md flex-shrink-0 ${
                              isUp
                                ? 'bg-emerald-500/15 ring-1 ring-emerald-500/30'
                                : 'bg-rose-500/15 ring-1 ring-rose-500/30'
                            }`}
                          >
                            <TypeIcon
                              className={`h-3.5 w-3.5 ${
                                isUp ? 'text-emerald-400' : 'text-rose-400'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-[10px] font-medium text-white truncate">
                                {u.name}
                              </p>
                              <span
                                className={`text-[8px] px-1 py-0.5 rounded-full ${
                                  isUp
                                    ? 'bg-emerald-500/10 text-emerald-400'
                                    : 'bg-rose-500/10 text-rose-400'
                                }`}
                              >
                                {isUp ? t('upsell') : t('downsell')}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <p className="text-[9px] text-zinc-500 truncate">
                                {u.offer?.name} • {u.offer?.price_formatted}
                              </p>
                            </div>
                            {hasAnyConnection && (
                              <div className="flex items-center gap-1.5 mt-1">
                                {hasAccept && (
                                  <span className="inline-flex items-center gap-0.5 text-[8px] text-emerald-400/70 bg-emerald-500/10 px-1 py-0.5 rounded">
                                    <Link2 className="h-2 w-2" />
                                    {t('accept_label')}
                                  </span>
                                )}
                                {hasDecline && (
                                  <span className="inline-flex items-center gap-0.5 text-[8px] text-red-400/70 bg-red-500/10 px-1 py-0.5 rounded">
                                    <Link2 className="h-2 w-2" />
                                    {t('decline_label')}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {totalListPages > 1 && (
                    <div className="flex items-center justify-between pt-1 border-t border-[#27272a]">
                      <span className="text-[9px] text-zinc-500">
                        {filteredUpsells.length === 1 ? t('connection_upsells_count', { count: filteredUpsells.length }) : t('connection_upsells_count_plural', { count: filteredUpsells.length })} • {t('connection_page', { current: listPage, total: totalListPages })}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setListPage((p) => Math.max(1, p - 1))}
                          disabled={listPage === 1}
                          className="flex h-6 w-6 items-center justify-center rounded-md bg-[#09090b] border border-[#27272a] hover:border-zinc-600 transition-colors disabled:opacity-30"
                        >
                          <ChevronLeft className="h-3 w-3 text-zinc-400" />
                        </button>
                        <button
                          onClick={() => setListPage((p) => Math.min(totalListPages, p + 1))}
                          disabled={listPage === totalListPages}
                          className="flex h-6 w-6 items-center justify-center rounded-md bg-[#09090b] border border-[#27272a] hover:border-zinc-600 transition-colors disabled:opacity-30"
                        >
                          <ChevronRight className="h-3 w-3 text-zinc-400" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {mode === 'external' && (
                <div className="space-y-3">
                  <button
                    onClick={() => setMode('menu')}
                    className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    {t('connection_back')}
                  </button>

                  <div className="space-y-1">
                    <label className="text-[11px] text-zinc-400">{t('connection_redirect_url')}</label>
                    <Input
                      placeholder="https://example.com/page"
                      value={externalUrl}
                      onChange={(e) => setExternalUrl(e.target.value)}
                      onFocus={(e) => {
                        if (!e.target.value) {
                          setExternalUrl('https://');
                        }
                      }}
                      className="text-xs h-9"
                    />
                  </div>

                  <div className="rounded-md bg-blue-500/5 border border-blue-500/10 px-2.5 py-2">
                    <p className="text-[10px] text-blue-400/80">
                      {t(direction === 'accept' ? 'connection_redirect_info_accept' : 'connection_redirect_info_decline')}
                    </p>
                  </div>

                  <Button
                    onClick={handleConnectExternal}
                    disabled={!externalUrl || isConnecting}
                    className="w-full"
                    size="sm"
                  >
                    {isConnecting ? t('connection_connecting') : t('connection_set_redirect')}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
